import React from 'react';
import { Badge } from '@/components/ui/Badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';

interface TaskDetail {
  task_id: string;
  user_id: number | null;
  username: string | null;
  email: string | null;
  task_type: string;
  backend: string | null;
  model_used: string | null;
  priority: string | null;
  status: string;
  duration: number | null;
  tokens_used: number;
  input_summary: string | null;
  error_message: string | null;
  created_at: string | null;
}

interface TaskDetailModalProps {
  open: boolean;
  onClose: () => void;
  task: TaskDetail | null;
}

const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-0">
    <span className="text-sm text-gray-500 w-24 flex-shrink-0">{label}</span>
    <span className="text-sm text-gray-900 flex-1 break-all">{value}</span>
  </div>
);

const TaskDetailModal: React.FC<TaskDetailModalProps> = ({ open, onClose, task }) => {
  if (!task) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" onClose={onClose}>
        <DialogHeader>
          <DialogTitle>작업 상세 정보</DialogTitle>
        </DialogHeader>

        <div className="space-y-1">
          <InfoRow label="Task ID" value={<span className="font-mono text-xs">{task.task_id}</span>} />
          <InfoRow label="상태" value={
            <Badge variant={task.status === 'completed' ? 'success' : 'error'}>
              {task.status === 'completed' ? '완료' : '실패'}
            </Badge>
          } />
          <InfoRow label="요청자" value={
            task.username ? `${task.username} (${task.email || ''})` : '-'
          } />
          <InfoRow label="작업 유형" value={task.task_type} />
          <InfoRow label="Provider" value={task.backend || '-'} />
          <InfoRow label="모델" value={task.model_used || '-'} />
          <InfoRow label="우선순위" value={task.priority || '-'} />
          <InfoRow label="소요 시간" value={
            task.duration !== null ? `${task.duration.toFixed(3)}초` : '-'
          } />
          <InfoRow label="토큰 사용" value={task.tokens_used > 0 ? `${task.tokens_used} tokens` : '-'} />
          <InfoRow label="요청 시간" value={
            task.created_at ? new Date(task.created_at).toLocaleString('ko-KR') : '-'
          } />
        </div>

        {task.input_summary && (
          <div className="mt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">입력 요약</h4>
            <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600 whitespace-pre-wrap">
              {task.input_summary}
            </div>
          </div>
        )}

        {task.error_message && (
          <div className="mt-4">
            <h4 className="text-sm font-medium text-red-700 mb-2">에러 메시지</h4>
            <div className="bg-red-50 rounded-lg p-3 text-sm text-red-600 whitespace-pre-wrap">
              {task.error_message}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TaskDetailModal;
